import React, { useState } from 'react';
import { Box, Button, Card, CardContent, Chip, CircularProgress, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Translate } from 'react-localize-redux';
import AutoQcScoreChip from './AutoQcScoreChip';
import ClinicalImageTile from '../../../components/general/ClinicalImageTile';
import UnavailableImage from '../../../components/general/UnavailableImage';
import ShotTypeLabel from './ShotTypeLabel';
import { QcOutcome, ReviewImage, isCurtained } from './imageMeta';
import { AUDIT_TINT } from './qcTokens';

/** The audit trail of a reviewer correcting the device's identifiable call (SKIN-237). */
export interface IdentifiableCorrection {
    actorName: string;
    correctedAt: string;
    reason: string;
    from: boolean;
    to: boolean;
}

interface ImageReviewCardProps {
    image: ReviewImage;
    /**
     * 1-based position within the visit, as rendered in the meta line. A prop rather than
     * a field on `ReviewImage` because it is a fact about this gallery's ordering, not
     * about the photograph — and it can only be known after duplicates are collapsed.
     */
    number: number;
    /**
     * The reviewer's verdict, which frames the card. Its own prop rather than a field on
     * `ReviewImage`: the frame has to track what the reviewer is editing right now, and a
     * field on the image would be a second, staler answer to the same question.
     */
    outcome?: QcOutcome | null;
    /** Whether this reviewer holds `VIEW_IDENTIFIABLE_IMAGES`. */
    canViewIdentifiable: boolean;
    /** The visit is approved (LOCKED): render marks as static text, offer no actions. */
    readOnly?: boolean;
    /** The correction already applied to this image's flag, if any. */
    correction?: IdentifiableCorrection;
    /**
     * Open the correction dialog for this photograph (SKIN-243). Absent means the
     * correction is not on offer at all — the caller withholds it from a reviewer without
     * `VIEW_IDENTIFIABLE_IMAGES` and on an approved visit. Absent rather than disabled:
     * §5 of the design brief shows capabilities progressively, never greyed out.
     */
    onCorrectIdentifiable?: (encryptedFileUuid: string) => void;
    /** Re-run retrieval + decryption for this one image. Absent when retry is not offered. */
    onRetry?: (encryptedFileUuid: string) => void;
    /** Open the enlarged view. Absent when the image cannot be shown. */
    onEnlarge?: (encryptedFileUuid: string) => void;
    /** The assessment controls (SKIN-241) render inside the card, under the meta. */
    children?: React.ReactNode;
}

/**
 * The card is sized from the handoff, not from the thumbnail.
 *
 * It used to be `thumbnail + 32` = 232px, and that was the real cause of a defect the
 * design review found: the feedback categories are chips inside this card, and at 232px
 * five of twelve labels clipped — including "Identifiable, but not ticked in the app",
 * a safeguarding flag the reviewer was being asked to tick without being able to read it.
 * The handoff's card measures 378px and lays two per row; at that width the same chips fit.
 * Widening the container is the fix the design already made; wrapping the labels was only
 * the symptom's.
 */
const CARD_WIDTH = 372;
/** 4:3, as the handoff draws every thumbnail. */
const THUMB_HEIGHT = Math.round((CARD_WIDTH * 3) / 4);

/**
 * SKIN-240 — one clinical photograph on the visit review screen.
 *
 * The unit of QC is the IMAGE: the score, the identifiable flag and its correction, and
 * the reviewer's mark all belong to this card. The visit around it is only the
 * container for reviewing a batch in one sitting.
 *
 * The card is presentational. It never fetches, decrypts or writes — it is handed a
 * `ReviewImage` whose `blobUrl` the decrypt pipeline already produced, and it reports
 * intent upward. That is what lets every state below exist in the catalog before any
 * of it is wired to a route.
 */
export default function ImageReviewCard({
    image,
    number,
    outcome,
    canViewIdentifiable,
    readOnly = false,
    correction,
    onCorrectIdentifiable,
    onRetry,
    onEnlarge,
    children,
}: ImageReviewCardProps) {
    const [revealed, setRevealed] = useState(false);

    const curtained = isCurtained(image) && !revealed;
    const borderColour = outcome === 'correct'
        ? 'success.main'
        : outcome === 'needs'
            ? 'warning.main'
            : 'divider';

    return (
        <Card
            variant="outlined"
            data-testid="image-review-card"
            data-image-uuid={image.encryptedFileUuid}
            data-outcome={outcome ?? 'unassessed'}
            sx={{
                width: CARD_WIDTH,
                borderColor: borderColour,
                // The frame is the only signal that survives a scan down a 21-image
                // gallery, so it is 2px rather than the 1px `outlined` default.
                borderWidth: outcome ? 2 : 1,
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    // minHeight, not height: the decrypt-failure state carries an error
                    // string of unbounded length, and a hard ceiling clips exactly the
                    // text that tells a reviewer whether to bother retrying.
                    minHeight: THUMB_HEIGHT,
                    bgcolor: 'grey.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                }}
            >
                <Thumbnail
                    image={image}
                    curtained={curtained}
                    canViewIdentifiable={canViewIdentifiable}
                    onReveal={() => setRevealed(true)}
                    onRetry={onRetry}
                    onEnlarge={onEnlarge}
                />

                {/*
                  * The flag at a glance, in the corner of the photograph it describes —
                  * the handoff puts it here as well as in the row below, and the pair is
                  * not redundant: the corner is what a reviewer scans down a gallery,
                  * the row is what they act on.
                  *
                  * Suppressed while the curtain is down, which already says the image is
                  * identifiable in words. Suppressed too when the flag is `null`: the
                  * device recorded no opinion, and a chip is a claim.
                  */}
                {!curtained && image.identifiable !== null && (
                    <Chip
                        size="small"
                        data-testid="identifiable-badge"
                        data-identifiable={image.identifiable}
                        color={image.identifiable ? 'warning' : 'default'}
                        label={(
                            <Translate
                                id={image.identifiable
                                    ? 'hospital.quality-check.review.identifiable'
                                    : 'hospital.quality-check.review.non-identifiable'}
                            />
                        )}
                        sx={{ position: 'absolute', top: 8, right: 8 }}
                    />
                )}
            </Box>

            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="subtitle2" data-testid="image-meta-type">
                    {`${number}. `}
                    <ShotTypeLabel fieldName={image.fieldName} />
                </Typography>

                {/*
                  * Target and body region come from the eCRF and are both optional
                  * answers. The line renders only the parts that exist and disappears
                  * entirely when neither does — a card is not the place to advertise a
                  * gap in the form with an empty separator.
                  */}
                {(image.bodyRegion || image.target) && (
                    <Typography variant="caption" color="text.secondary" display="block" data-testid="image-meta-context">
                        {[image.bodyRegion, image.target].filter(Boolean).join(' · ')}
                    </Typography>
                )}

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    <AutoQcScoreChip score={image.qualityScore} />
                </Box>

                <IdentifiableRow
                    image={image}
                    correction={correction}
                    onCorrect={onCorrectIdentifiable}
                />

                {/*
                  * At card level, not inside the identifiable row. "Approved — read only"
                  * is a statement about the VISIT, and nesting it under a row that early-
                  * returns for an image with no identifiable opinion made it disappear on
                  * exactly those cards — the note silently absent on an approved visit is
                  * worse than never having had one, because its presence elsewhere on the
                  * page implies its absence here means something.
                  */}
                {readOnly && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        data-testid="read-only-note"
                        sx={{ mt: 1 }}
                    >
                        <Translate id="hospital.quality-check.review.read-only" />
                    </Typography>
                )}

                {children}
            </CardContent>
        </Card>
    );
}

/**
 * The thumbnail slot. Its branches are ordered by what the reviewer is allowed to see,
 * not by where the bytes happen to be.
 *
 * The curtain is checked FIRST and on the flag alone. A reviewer without
 * `VIEW_IDENTIFIABLE_IMAGES` never receives a presigned URL for a flagged image, so
 * that image also arrives with `bytes.state === 'failed'` — and reporting it as a
 * decryption failure would be both wrong and alarming, since nothing failed. It was
 * refused, and the reason is a permission.
 */
export function Thumbnail({
    image,
    curtained,
    canViewIdentifiable,
    onReveal,
    onRetry,
    onEnlarge,
    height = THUMB_HEIGHT,
    renderReady,
}: {
    image: ReviewImage;
    curtained: boolean;
    canViewIdentifiable: boolean;
    onReveal: () => void;
    onRetry?: (encryptedFileUuid: string) => void;
    onEnlarge?: (encryptedFileUuid: string) => void;
    /**
     * SKIN-784 — the lesion review dialog draws the same slot large. Exported and sized
     * rather than copied: the order of these branches (curtain before failure) is a
     * permission rule, and a second copy is a second place for it to go wrong.
     */
    height?: number;
    /**
     * Draw the pixels some other way once every guard above has passed — the review dialog
     * draws them zoomable. A render prop rather than a check at the call site, so the caller
     * never has to restate which images it may show.
     */
    renderReady?: (blobUrl: string) => React.ReactNode;
}) {
    if (curtained) {
        return (
            <Box sx={{ textAlign: 'center', px: 2 }} data-testid="pii-curtain">
                <LockOutlinedIcon color="action" />
                <Typography variant="caption" display="block" sx={{ fontWeight: 600, mt: 0.5 }}>
                    <Translate id="hospital.quality-check.review.identifiable-hidden" />
                </Typography>
                {canViewIdentifiable ? (
                    <Button
                        size="small"
                        onClick={onReveal}
                        data-testid="reveal-image"
                        sx={{ mt: 0.5 }}
                    >
                        <Translate id="hospital.quality-check.review.show-image" />
                    </Button>
                ) : (
                    // The restricted tier can still mark this image and approve the
                    // visit. Saying so stops the curtain reading as "you cannot review
                    // this one".
                    <Typography variant="caption" color="text.secondary" display="block" data-testid="curtain-restricted">
                        <Translate id="hospital.quality-check.review.identifiable-restricted" />
                    </Typography>
                )}
            </Box>
        );
    }

    if (image.bytes.state === 'loading') {
        return (
            <Box sx={{ textAlign: 'center' }} data-testid="image-decrypting">
                <CircularProgress size={20} />
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                    <Translate id="hospital.quality-check.review.decrypting" />
                </Typography>
            </Box>
        );
    }

    if (image.bytes.state === 'failed') {
        // Straight to `UnavailableImage`, which has drawn this failure for both surfaces
        // since SKIN-206 and already tells the two stages apart — "could not be decoded"
        // claims the bytes we hold are unreadable, which for a clinical image is worth
        // being accurate about, while a presigned-URL failure says nothing about the bytes.
        // SKIN-240 gave it the retry the reviewer needs, since the usual cause is an
        // expired URL and a retry fixes it far more often than a page reload.
        //
        // Not routed through the tile: this branch already knows the outcome, and handing
        // it to a component that would only take the union apart and pass these same four
        // props on is indirection with nothing on the other side of it. The clinical record
        // keeps the mirror image of this early return.
        return (
            <UnavailableImage
                failedAt={image.bytes.failedAt}
                error={image.bytes.error}
                size={height}
                onRetry={onRetry ? () => onRetry(image.encryptedFileUuid) : undefined}
            />
        );
    }

    // Past all three guards the bytes exist and this reviewer may see them.
    if (renderReady) return <React.Fragment>{renderReady(image.bytes.blobUrl)}</React.Fragment>;
    return (
        <ClinicalImageTile
            blobUrl={image.bytes.blobUrl}
            width="100%"
            height={height}
            onEnlarge={onEnlarge ? () => onEnlarge(image.encryptedFileUuid) : undefined}
        />
    );
}

/**
 * The identifiable flag and, when one exists, the permanent record of it being
 * corrected.
 *
 * The audit line is not conditional on anything but the correction existing — not on the
 * current flag, not on whether the visit is locked. A correction is a claim a named person
 * made about a patient's photograph, and the device's original call is never erased by it,
 * so both survive on the card for anyone who opens the visit afterwards.
 *
 * SKIN-243 put the action that produces a correction on this row, next to the flag it
 * changes. SKIN-784 exported it for the lesion review dialog, where the flag is judged on
 * the large photograph — one row, so the card and the dialog cannot word it differently.
 */
export function IdentifiableRow({
    image,
    correction,
    onCorrect,
}: {
    image: ReviewImage;
    correction?: IdentifiableCorrection;
    onCorrect?: (encryptedFileUuid: string) => void;
}) {
    // No early return. The row has a defined rendering for all three flag states, including
    // `identifiable-unknown`, so hiding it only ever hid it from the reviewer who could not
    // act on it anyway — while "the device recorded no opinion" is the state where an
    // unnoticed face is most likely to be sitting.

    return (
        <Box sx={{ mt: 1 }} data-testid="identifiable-row">
            {/*
              * The handoff's row: the flag on the left, the action on the right, inside
              * one bordered strip. The pairing is the point — the correction is offered
              * beside the claim it changes, not below the card as a loose button.
              */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.25,
                    px: 1.5,
                    py: 1,
                    bgcolor: 'grey.50',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                }}
            >
                {/*
                  * Three states, not two. `null` means the device recorded no opinion — a
                  * legacy upload, or a value the API rejected as out of range — and printing
                  * that as "No" would turn silence into an assurance that a patient is not
                  * identifiable in their own photograph. The same distinction the score chip
                  * already honours.
                  */}
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    <Translate id="hospital.quality-check.review.identifiable" />
                    {': '}
                    <Box
                        component="b"
                        sx={{ color: image.identifiable ? 'warning.dark' : 'text.primary' }}
                    >
                        {image.identifiable === null
                            ? <Translate id="hospital.quality-check.review.identifiable-unknown" />
                            : <Translate id={image.identifiable ? 'general.yes' : 'general.no'} />}
                    </Box>
                </Typography>

                {onCorrect && (
                    <Button
                        size="small"
                        color="info"
                        startIcon={<EditOutlinedIcon fontSize="small" />}
                        data-testid="correct-identifiable"
                        onClick={() => onCorrect(image.encryptedFileUuid)}
                        sx={{ fontWeight: 600, flexShrink: 0 }}
                    >
                        <Translate id="hospital.quality-check.review.correct-flag" />
                    </Button>
                )}
            </Box>
            {correction && (
                // The handoff gives the trail its own tinted block with the same pencil
                // the action carries, rather than a grey italic afterthought: it is the
                // record of a person changing a claim about a patient's photograph.
                <Box
                    data-testid="identifiable-audit-line"
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 0.875,
                        mt: 0.875,
                        px: 1.25,
                        py: 0.875,
                        bgcolor: AUDIT_TINT.bg,
                        borderRadius: 1,
                    }}
                >
                    <EditOutlinedIcon fontSize="small" sx={{ flex: '0 0 auto', mt: '1px', color: AUDIT_TINT.fg }} />
                    <Typography variant="caption" color="text.secondary">
                        {/*
                          * Composed rather than interpolated: nothing in this codebase uses
                          * react-localize-redux's `data` placeholders, and introducing the
                          * first one here would make this card the only place a missing
                          * placeholder could silently render `${actor}` to a reviewer.
                          */}
                        <Translate id="hospital.quality-check.review.corrected-by" />
                        {` ${correction.actorName}, ${new Date(correction.correctedAt).toLocaleDateString()} — ${correction.reason}`}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

